import { ConversationChannel } from "@remi/enums/conversation/channel";

import { hasCapability, type User } from "../base";
import { NON_EXISTENT_ID } from "../utils/types";

// These capability gates bypass a DAO-level permission check on conversations.
// ESTIMATOR is in REMI_INTERNAL org which is on every conversation, so they
// effectively access all conversations — only add roles here that genuinely
// need all-conversation access.
export const conversation = {
  read: (user: User) => hasCapability(user, "conversation.read.all") || hasCapability(user, "conversation.read.org"),
  // Internal staff viewing another org's conversations at the org level; unlike read.org, which
  // lets an external org read conversations its own members participate in.
  readOrganizationLevel: (user: User) => hasCapability(user, "conversation.read.organizationLevel"),
  view: (user: User) => hasCapability(user, "conversation.view"),
  list: (user: User) => hasCapability(user, "conversation.list"),
  sendMessage: (user: User) => hasCapability(user, "conversation.sendMessage"),
  sendPartnerMessage: (user: User) => hasCapability(user, "conversation.message.partner.send"),
  update: (user: User) => hasCapability(user, "conversation.update"),
  create: (user: User) => hasCapability(user, "conversation.create"),
};

export const conversationWhere = ({ user, includeDeleted = false }: { user: User; includeDeleted?: boolean }) => {
  const baseWhere = includeDeleted
    ? {}
    : {
        deletedAt: null,
      };
  if (hasCapability(user, "conversation.read.all")) {
    if (!hasCapability(user, "contractorInternalFields.read")) {
      return { ...baseWhere, NOT: { channel: ConversationChannel.INTERNAL_CONTRACTOR_REMI } };
    }
    return baseWhere;
  }

  const accessClauses: Array<
    | { organizations: { some: { organizationId: string } } }
    | { persons: { some: { person: { organizationId: string } } } }
  > = [];

  if (hasCapability(user, "conversation.read.org")) {
    accessClauses.push({
      organizations: {
        some: {
          organizationId: user.person.organizationId,
        },
      },
    });
    accessClauses.push({
      persons: {
        some: {
          person: {
            organizationId: user.person.organizationId,
          },
        },
      },
    });
  }

  if (accessClauses.length === 0) {
    return { ...baseWhere, AND: [{ id: NON_EXISTENT_ID }] };
  }
  return { ...baseWhere, NOT: { channel: ConversationChannel.INTERNAL_CONTRACTOR_REMI }, OR: accessClauses };
};
